import { requestId } from "../api/trackPayloads";
import ButtonBase from "@mui/material/ButtonBase";
import useSetTrackMembership from "../hooks/useSetTrackMembership";
import Alert from "@mui/material/Alert";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import TextInput from "../ui/TextInput";
import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  API_BASE,
  fetchSearch,
  fetchJson,
  SEARCH_PAGE_SIZE,
  resolveTrackArtists,
} from "../utils";
import { localReturnPath } from "../utils/navigation";
import useSearchResults from "../hooks/useSearchResults";
const loadTracks = async (query, page, signal) => {
  const data = await fetchSearch({
    query,
    page,
    types: "track",
    signal,
  });
  return data.results || [];
};
export default function AddSetTrack() {
  const [params] = useSearchParams();
  const setId = params.get("set");
  const returnTo = localReturnPath(params.get("return"));
  if (!setId)
    return (
      <Alert role="alert" severity="error">
        Choose a set before adding tracks.
      </Alert>
    );
  return (
    <AddSetTrackContent
      key={`${setId}:${returnTo}`}
      setId={setId}
      returnTo={returnTo}
    />
  );
}
function AddSetTrackContent({ setId, returnTo }) {
  const form = useForm({
    defaultValues: {
      query: "",
    },
    mode: "onChange",
  });
  const [query, setQuery] = useFormField(form, "query");
  const {
    results: tracks,
    hasMore,
    loading,
    fetched,
    error,
    loadMore,
  } = useSearchResults({
    query,
    enabled: true,
    loadPage: loadTracks,
    pageSize: SEARCH_PAGE_SIZE,
  });
  const [pending, setPending] = React.useState(false);
  const [mutationError, setMutationError] = React.useState(null);
  const busy = React.useRef(false);
  const active = React.useRef(true);
  const membership = useSetTrackMembership(setId);
  const loadMoreRef = React.useRef(null);
  React.useEffect(() => {
    if (
      !loadMoreRef.current ||
      !hasMore ||
      loading ||
      error ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "0px 0px 600px 0px" },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, error, loadMore, tracks.length]);
  const navigate = useNavigate();
  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const handleAdd = async (trackId) => {
    if (
      busy.current ||
      membership.loading ||
      membership.error ||
      membership.ids.has(String(trackId))
    )
      return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    try {
      await fetchJson(`${API_BASE}/user-sets/${setId}/tracks/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          track_id: requestId(trackId),
        }),
      });
      if (active.current) navigate(returnTo);
    } catch (err) {
      if (active.current) setMutationError(err);
    } finally {
      busy.current = false;
      if (active.current) setPending(false);
    }
  };
  return (
    <div className="add-set-track-page">
      {mutationError && (
        <Alert role="alert" severity="error">
          {mutationError.message}
        </Alert>
      )}
      <div className="search-bar">
        <div className="album-search">
          <TextInput
            autoFocus
            type="text"
            aria-label="Search tracks"
            placeholder="Search tracks..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="search-icon">
            <SearchIcon />
          </span>
        </div>
      </div>
      {membership.loading && <p role="status">Checking set tracks…</p>}
      {membership.error && (
        <Alert severity="error">
          Could not load set tracks: {membership.error.message}{" "}
          <Button onClick={membership.retry}>Retry set tracks</Button>
        </Alert>
      )}
      <ul className="album-grid">
        {tracks.map((track) => {
          const alreadyAdded = membership.ids.has(String(track.id));
          return (
            <li
              key={track.id}
              className={`album-tile compact-catalog-card collection-track-card${alreadyAdded ? " unavailable-track" : ""}`}
            >
              <ButtonBase
                className="catalog-add-track"
                disableRipple
                aria-label={
                  alreadyAdded
                    ? `${track.title}: Track already in Set`
                    : `Add ${track.title}`
                }
                disabled={
                  pending ||
                  membership.loading ||
                  !!membership.error ||
                  alreadyAdded
                }
                onClick={() => handleAdd(track.id)}
              >
                <div className="album-thumb">
                  <img
                    src={`${API_BASE}/albums/${track.album?.id ?? track.album ?? track.album_id}/image/`}
                    alt={`${track.title} artwork`}
                    loading="lazy"
                  />
                </div>
                <div className="album-info">
                  <div className="album-title">
                    <span>{track.title}</span>
                  </div>
                  <div className="album-artist">
                    {resolveTrackArtists(track)
                      .map((artist) => artist.name)
                      .join(", ")}
                  </div>
                  {alreadyAdded && (
                    <div className="track-subtitle">Track already in Set</div>
                  )}
                  <div className="track-subtitle">
                    {track.album_title || track.album?.title}
                  </div>
                </div>
              </ButtonBase>
            </li>
          );
        })}
        {hasMore && tracks.length > 0 && (
          <li
            ref={loadMoreRef}
            className="search-scroll-sentinel"
            aria-hidden="true"
          />
        )}
        {loading && (
          <li
            className="album-tile"
            style={{
              textAlign: "center",
            }}
          >
            <span role="status">Loading...</span>
          </li>
        )}
        {!loading && !error && fetched && query && tracks.length === 0 && (
          <li
            className="album-tile"
            style={{
              textAlign: "center",
            }}
          >
            No results
          </li>
        )}
      </ul>
      {error && (
        <Alert role="alert" severity="error">
          Could not search tracks: {error.message}{" "}
          <Button className="btn" onClick={loadMore}>
            Retry search
          </Button>
        </Alert>
      )}
      {hasMore && !error && (
        <Button className="btn" disabled={loading} onClick={loadMore}>
          Load more tracks
        </Button>
      )}
    </div>
  );
}
